import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { RenewalsMatrixService } from '../../services/renewals-matrix.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, forkJoin, of, Subscription } from 'rxjs';
import { default as _rollupMoment } from 'moment';
import { distinctUntilChanged, switchMap } from 'rxjs/internal/operators';
import { WizardService } from 'src/app/wizard-bar/services/wizard.service';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { QuoteRequestState } from '@app/shared';
import { Store } from '@ngrx/store';
import { UserRole, UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { isEqual } from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { QuoteRequestStatus } from '@app/shared/models/quote-request-status';
import { RenewalsMatrixConstants } from '../../models/renewals-matrix-constants';
import { PricingSourceService, PricingSource } from '@app/shared/services/pricing-source.service';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import { SaleType } from '@app/shared/models/sale-type';
import { Address, QuoteConfiguration } from '@app/shared/models/customer-details';
import LogRocket from 'logrocket';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { ProductTypeConfiguration } from '@configuration/models/product-type-configuration';
import { UtilityHubConfiguration } from '@configuration/models/utilityhub-configuration';
import { CustomerType } from '@app/shared/models/customer-type';
import { defaultContractLengths } from '@app/shared/models/default-contract-lengths';
import { ContractLength } from '@app/shared/models/contract-length';
import { SalesType } from '@app/shared/enums/sales-type';
import { UtilityZones } from '@app/shared/models/utility-information';
import { UtilityInformationService } from '@app/shared/services/utility-information.service';
import { UtilityHubService } from '@app/shared/services/utilityhub.service';
import { CustomerTypeSize } from '@app/shared/models/customer-type-size';
import { AppState } from '@app/state/app.state';
import { loggedInUserSelector, loggedInUtilityHubIdentitySelector, configurationSelector } from '@app/state/app.selectors';
import {
  SetQuoteRequestType,
  SetUtilityUOM,
  SetCustomerType,
  SetCustomerTypeSize,
  SetSweetSpotCheckBoxState,
  SetPageName,
  SetSalesType,
  SetCommodityType,
  SupplierUtilityInformation,
  ClearUtility,
  ClearUtilityInformation,
  SetUtilityInformation,
  SetUtility,
  SetProductType,
  SetContractLengthsSelected,
} from '@utility-information/state/actions/utility-information.actions';
import {
  isDynamicSelector,
  productTypesFromConfigSelector,
  commodityTypeSelector,
  zonesSelector,
  utilityUOMSelector,
  isMatrixJourneyElectricEnabledSelector,
  isMatrixJourneyGasEnabledSelector,
} from '@utility-information/state/selectors/utility-information.selectors';
import { SetOriginalQuoteRequestId } from '@app/renewals/state/actions/renewals-module.actions';
import { originalQuoteRequestIdSelector } from '@app/renewals/state/selectors/renewals-module.selectors';

@Component({
  selector: 'app-renewals-matrix-customer-details',
  templateUrl: './renewals-matrix-customer-details.component.html',
  styleUrls: ['./renewals-matrix-customer-details.component.scss'],
})
export class RenewalsMatrixCustomerDetailsComponent implements OnInit, OnDestroy {
  constructor(
    private fb: FormBuilder,
    public renewalsMatrixService: RenewalsMatrixService,
    public quoteRequestService: QuoteRequestService,
    private route: ActivatedRoute,
    private router: Router,
    public wizardService: WizardService,
    private store: Store<AppState>,
    protected dialog: MatDialog,
    private pricingSourceService: PricingSourceService,
    private utilityInformationService: UtilityInformationService,
    private utilityHubService: UtilityHubService
  ) {
    this.store.dispatch(new SetQuoteRequestType(this.quoteRequestId, QuoteRequestType.Matrix));
    this.isDynamic$ = this.store.select(isDynamicSelector);
    this.productTypes$ = this.store.select(productTypesFromConfigSelector).pipe(
      distinctUntilChanged((prev, curr) => JSON.stringify(prev.map((x) => x)) === JSON.stringify(curr.map((y) => y))),
      switchMap((products) => {
        if (products?.length === 1) {
          this.selectSingleProduct(products[0]);
          return of(products);
        }
        this.productTypeCtrl.enable();
        return of(products);
      })
    );
    this.commodityType$ = this.store.select(commodityTypeSelector).pipe(
      switchMap((commodityType) => {
        if (commodityType === CommodityType.Gas) {
          this.productTypeCtrl.disable();
          this.productTypeCtrl.clearValidators();
        } else {
          this.productTypeCtrl.addValidators(Validators.required);
          this.productTypeCtrl.enable();
        }
        this.pricingSource = this.pricingSourceService.fetchPricingSource(
          QuoteRequestType.Matrix,
          UserRole[this.userIdentity.currentOrganisation.role],
          this.utilityHubConfiguration?.supplierConfiguration.pricing.powwrSupplier,
          this.commodityCtrl.value
        );
        this.setControlVisibility();
        return of(commodityType);
      })
    );
    this.zones$ = this.store.select(zonesSelector);
  }

  frmQuoteConfiguration: FormGroup;
  quoteData;
  quoteRequestId: any | null;
  quoteRequestState: QuoteRequestState;
  defaultContractLengthButtonsToShow = defaultContractLengths;
  parsedData: { something: string };
  disabled: boolean = false;
  userIdentity: UtilityHubIdentity;
  utilityHubConfiguration: UtilityHubConfiguration;
  isPriceRequested: boolean;
  loggedInUser$: Subscription;
  uhIdentity$: Subscription;
  brokerageValueChanges$: Subscription;
  locationValueChanges$: Subscription;
  load$: Subscription;
  saveMatrixQuoteConfig$: Subscription;
  showControls = {
    showIso: true,
    showZone: true,
    showBundledRateCheckbox: false,
    loadingUtilities: false,
    emitEventOnValueChange: true,
  };
  loadingUtilities: boolean = false;
  emitEventOnValueChange = true;
  pricingSource: PricingSource;
  PricingSourceEnum = PricingSource;
  showAnnualUsageControl: boolean;
  dunsEnabled: boolean;
  showAllButton: boolean;
  isDynamic$: Observable<boolean>;
  productTypes$: Observable<ProductTypeConfiguration[]>;
  commodityType$: Observable<string>;
  commodityTypeEnum = CommodityType;
  commodity: CommodityType = CommodityType.Electricity;
  commodityType = CommodityType;
  showValidation: boolean;
  isGasEnabled: boolean;
  isElectricEnabled: boolean;
  utilityUOM$: Subscription;
  uom: string;
  address: Address;
  zones: string[];
  weatherZones: string[];
  utilityZones: UtilityZones[];
  quoteRequestType: QuoteRequestType = QuoteRequestType.Matrix;
  stateCode: string;
  zipCode: string;
  city: string;
  zone: string;
  utilityShort: string;
  utilities: string[];
  iso: string;
  utilities$: Subscription;
  zones$: Observable<string[]>;
  supplierUtilityModel$: Subscription;
  externalId: string;
  originalQuoteRequestIdSelector$: Subscription;
  originalQuoteRequestId: number | null;

  get contractLengths() {
    return this.frmQuoteConfiguration.get('contractLengths');
  }

  get commodityCtrl() {
    return this.frmQuoteConfiguration.get('commodityType');
  }

  get productTypeCtrl() {
    return this.frmQuoteConfiguration.get('productType');
  }

  get utilityDetailsCtrl() {
    return this.frmQuoteConfiguration.get('utilityDetails');
  }
  get getState() {
    return this.frmQuoteConfiguration.getRawValue().customerDetails?.location?.state ?? '';
  }

  get getZipCode() {
    return this.frmQuoteConfiguration.getRawValue().customerDetails?.location?.zipCode ?? '';
  }

  private initialFormValue: {};

  ngOnInit(): void {
    this.originalQuoteRequestIdSelector$ = this.store.select(originalQuoteRequestIdSelector).subscribe((originalQuoteRequestId) => {
      this.originalQuoteRequestId = originalQuoteRequestId;
    });

    this.utilityUOM$ = this.store.select(utilityUOMSelector).subscribe((x) => (this.uom = x));
    this.store.dispatch(new SetQuoteRequestType(this.quoteRequestId, QuoteRequestType.Matrix));
    this.store.dispatch(new SetUtilityUOM(this.quoteRequestId, 'kWh'));
    this.store.dispatch(new SetCustomerType(this.quoteRequestId, CustomerType.Commercial));
    this.store.dispatch(new SetCustomerTypeSize(this.quoteRequestId, CustomerTypeSize.Small));
    this.store.dispatch(new SetSweetSpotCheckBoxState(this.quoteRequestId, true));
    this.store.dispatch(new SetPageName(this.quoteRequestId, 'companydetails'));
    this.store.dispatch(new SetSalesType(this.quoteRequestId, SalesType.renewal));
    this.store.select(isMatrixJourneyElectricEnabledSelector).subscribe((e) => (this.isElectricEnabled = e));
    this.store.select(isMatrixJourneyGasEnabledSelector).subscribe((g) => (this.isGasEnabled = g));
    this.setupForm();
    if (!this.isElectricEnabled) {
      this.commodityChanged(CommodityType.Gas);
      this.store.dispatch(new SetCommodityType(this.quoteRequestId, <CommodityType>'Gas'));
    } else {
      this.store.dispatch(new SetCommodityType(this.quoteRequestId, CommodityType.Electricity));
    }
    this.loadUtilityInformation();
    this.loggedInUser$ = this.store.select(loggedInUserSelector).subscribe((user) => {
      if (user && !user.expired) {
        this.uhIdentity$ = this.store.select(loggedInUtilityHubIdentitySelector).subscribe((uhUser) => {
          this.userIdentity = uhUser;
          this.store.select(configurationSelector).subscribe((config) => {
            this.utilityHubConfiguration = config;
            this.pricingSource = this.pricingSourceService.fetchPricingSource(
              QuoteRequestType.Matrix,
              UserRole[this.userIdentity.currentOrganisation.role],
              config.supplierConfiguration.pricing.powwrSupplier,
              this.commodityCtrl.value
            );
            this.dunsEnabled = config.supplierConfiguration.dunsLookupEnabled;
            this.setControlVisibility();
          });
        });
      }
    });

    this.externalId = this.utilityHubService.getExternalId(this.userIdentity);
    this.utilityHubService.getSupplierUtilityInformation(this.externalId).subscribe((resp) => {
      this.store.dispatch(new SupplierUtilityInformation(resp));
    });

    this.wizardService.bindStep(this.route, () => this.save());
    this.route.params.subscribe((x) => {
      this.quoteRequestId = null;
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
        this.load();
      } else {
        this.quoteRequestState = {
          organisationName: '',
          quoteRequestId: null,
        } as QuoteRequestState;
      }
    });
  }

  ngOnDestroy(): void {
    this.loggedInUser$?.unsubscribe();
    this.uhIdentity$?.unsubscribe();
    this.brokerageValueChanges$?.unsubscribe();
    this.locationValueChanges$?.unsubscribe();
    this.load$?.unsubscribe();
    this.saveMatrixQuoteConfig$?.unsubscribe();
    this.utilityUOM$?.unsubscribe();
    this.utilities$?.unsubscribe();
    this.supplierUtilityModel$?.unsubscribe();
    this.originalQuoteRequestIdSelector$?.unsubscribe();
  }

  load() {
    this.load$ = forkJoin({
      state: this.quoteRequestService.getQuoteRequestState(this.quoteRequestId),
      quoteConfiguration: this.renewalsMatrixService.getQuoteConfiguration(this.quoteRequestId),
    }).subscribe((res) => {
      this.quoteData = res.quoteConfiguration;
      this.quoteRequestState = res.state;
      this.isPriceRequested = res.state.status !== QuoteRequestStatus.InProgress;
      this.quoteData.saleType = SaleType.Renewal;
      this.frmQuoteConfiguration.patchValue(this.quoteData);
      this.frmQuoteConfiguration.get('commodityType').setValue(res.quoteConfiguration?.utilityDetails?.commodity);
      this.utilityDetailsCtrl.setValue(res.quoteConfiguration?.utilityDetails);
      this.address = res.quoteConfiguration.customerDetails.location;
      this.initialFormValue = this.frmQuoteConfiguration.getRawValue();
      this.store.dispatch(new SetUtilityUOM(this.quoteRequestId, res.quoteConfiguration?.utilityDetails?.uom));
      this.store.dispatch(new SetProductType(this.quoteRequestId, res.quoteConfiguration.productType));
      this.store.dispatch(new SetCommodityType(this.quoteRequestId, res.quoteConfiguration?.utilityDetails?.commodity));
      this.store.dispatch(new SetCustomerType(this.quoteRequestId, res.quoteConfiguration?.customerDetails?.customerType));
      this.store.dispatch(new SetCustomerTypeSize(this.quoteRequestId, res.quoteConfiguration?.customerDetails?.customerTypeSize));
      this.store.dispatch(new SetSalesType(this.quoteRequestId, res.quoteConfiguration?.saleType));
      this.store.dispatch(
        new SetContractLengthsSelected(
          this.quoteRequestId,
          res.quoteConfiguration.contractLengths.map((x) => ({ months: x, selected: true } as ContractLength))
        )
      );
      this.store.dispatch(new SetOriginalQuoteRequestId(this.quoteRequestState.renewedFromQuoteId));

      this.stateCode = res.quoteConfiguration?.customerDetails?.location?.state;
      this.commodity = res.quoteConfiguration?.utilityDetails?.commodity;
      this.zipCode = res.quoteConfiguration?.customerDetails?.location?.zipCode;
      this.utilityShort = res.quoteConfiguration?.utilityDetails?.utilityShort;
      this.zone = res.quoteConfiguration.utilityDetails.zone;
      this.loadUtilityInformation();
      if (this.isReadonly()) {
        this.disableForm();
      }
    });
  }

  commodityChanged(commodity: any) {
    this.commodity = <CommodityType>commodity.value;
    this.showControls = { ...this.showControls };
    this.store.dispatch(new SetCommodityType(this.quoteRequestId, <CommodityType>commodity.value));
    if (this.address) {
      this.storeUtilityInformation(this.address);
    }
  }

  storeUtilityInformation(address: Address) {
    this.stateCode = address.state;
    this.zipCode = address.zipCode;
    this.city = address.city;
    this.address = address;
    this.store.dispatch(new ClearUtility(this.quoteRequestId));
    this.store.dispatch(new ClearUtilityInformation(this.quoteRequestId));
    this.store.dispatch(
      new SetUtilityInformation(this.quoteRequestId, address.state, QuoteRequestType.Matrix, address.zipCode, this.commodityCtrl.value)
    );
    this.store.dispatch(new SetUtility(this.quoteRequestId, this.utilityShort));
  }

  allowBundledRates() {
    if (this.frmQuoteConfiguration.get('state').value === 'TX') {
      return true;
    }
    return false;
  }

  setupForm() {
    const brokerage = new FormControl('', Validators.maxLength(255));
    const broker = new FormControl('', Validators.maxLength(255));
    const sweetSpotCheckbox = new FormControl(true);
    const saleType = new FormControl({ value: SaleType.Renewal, disabled: true });
    const contractLengths = new FormControl(
      { value: this.defaultContractLengthButtonsToShow, disabled: this.disabled },
      Validators.required
    );
    const utilityDetails = new FormControl('');
    const annualUsage = new FormControl('', [Validators.required, Validators.pattern(/^\d+\.{0,1}\d{0,2}$/)]);
    broker.disable(); // Initially disabled until there is text in brokerage

    this.store.dispatch(new SetCustomerType(this.quoteRequestId, CustomerType.Commercial));
    this.frmQuoteConfiguration = this.fb.group({
      brokerage,
      broker,
      customerDetails: [''],
      sweetSpotCheckbox,
      productType: [''],
      commodityType: this.isElectricEnabled ? [CommodityType.Electricity] : [CommodityType.Gas],
      contractLengths,
      utilityDetails,
      saleType,
      annualUsage,
    });

    // Enable or disable broker depending brokerage.
    this.brokerageValueChanges$ = brokerage.valueChanges.subscribe((x) => {
      if (!x) {
        broker.setValue('');
        broker.disable();
      } else {
        broker.enable();
      }
    });
  }

  save(): Observable<any> {
    const c = this.frmQuoteConfiguration.getRawValue();
    // Form is disabled for SM reviewing a BM quote request, except for the Zone field which is editable
    // A SM needs to be able to save changes to this field despite the form being disabled
    // https://powwr.atlassian.net/browse/IACBPT1-1191
    if (this.frmQuoteConfiguration.disabled && this.userIdentity.currentOrganisation.role !== UserRole.SupplierManager) {
      return of(true);
    }
    this.frmQuoteConfiguration.markAllAsTouched();
    return new Observable((obs) => {
      if (Object.values(this.frmQuoteConfiguration.controls).filter((x) => x.enabled && x.invalid).length === 0) {
        this.quoteData = {
          ...this.quoteData,
          ...this.frmQuoteConfiguration.getRawValue(),
          quoteRequestId: this.quoteRequestId ?? 0,
        };

        if (this.quoteData?.contractLengths?.length === undefined) this.quoteData.contractLengths = this.defaultContractLengthButtonsToShow;
        this.quoteData.contractLengths = removeDuplicates(this.quoteData.contractLengths);
        this.quoteData.utilityDetails.commodity = this.commodityCtrl.value;
        this.quoteData.utilityDetails.uom = this.uom;
        this.saveMatrixQuoteConfig$ = this.renewalsMatrixService
          .saveMatrixQuoteConfiguration(this.quoteData)
          .subscribe((quoteRequestId) => {
            if (!this.quoteData.quoteRequestId) {
              LogRocket.track('new_matrix_quote');
              this.router.navigate(['renewals-matrix', quoteRequestId, RenewalsMatrixConstants.wizardSteps[0].slug]).then(() => {
                this.route.params.subscribe(() => {
                  this.wizardService.bindStep(this.route, () => of(true)); // call again as we have just changed the route
                  this.wizardService.next();
                  obs.next(true);
                  obs.complete();
                });
              });
            } else {
              LogRocket.track('matrix_quote_configration_saved');
              obs.next(true);
              obs.complete();
            }
          });
      } else {
        obs.next(false);
        obs.complete();
      }
    });
  }

  next() {
    this.showValidation = true;
    this.wizardService.next(this.isQuoteRequestInvalidated());
  }

  back() {
    this.wizardService.previous(this.isQuoteRequestInvalidated());
  }

  isReadonly() {
    return this.quoteRequestService.isReadonly(this.quoteRequestId, this.quoteRequestState?.status, this.userIdentity?.id, this.quoteRequestState?.ownerUserId);
  }

  productSelectionChanged(productType: any) {
    this.store.dispatch(new SetProductType(this.quoteRequestId, productType));
  }

  allContractLengthsSelected(selected: any) {
    this.frmQuoteConfiguration.get('sweetSpotCheckbox').patchValue(selected);
    this.frmQuoteConfiguration.get('sweetSpotCheckbox').enable();
  }

  annualUsageIsLessThanZero() {
    return this.frmQuoteConfiguration.get('annualUsage')?.value <= 0;
  }

  isQuoteRequestInvalidated() {
    return (
      this.hasFormChanged() &&
      this.isPriceRequested &&
      this.initialFormValue &&
      (this.initialFormValue as QuoteConfiguration).utilityDetails.zone !== "I don't know" &&
      this.initialFormValue &&
      (this.initialFormValue as QuoteConfiguration).utilityDetails.zone !== this.frmQuoteConfiguration.get['zone']
    );
  }

  hasFormChanged() {
    return !isEqual(this.initialFormValue, this.frmQuoteConfiguration?.getRawValue()) && !isNaN(this.quoteRequestId);
  }

  orgNameChanged(name: string) {
    this.quoteRequestState = { ...this.quoteRequestState, organisationName: name };
  }

  customDetailsInitialised(frm: FormGroup) {
    this.frmQuoteConfiguration.setControl('customerDetails', frm);
  }

  setControlVisibility() {
    switch (this.pricingSource) {
      case PricingSource.SupplierMatrix: // custom | non dynamic | supplier manager
        this.frmQuoteConfiguration.get('contractLengths').disable();
        this.frmQuoteConfiguration.get('annualUsage').enable();
        break;
      case PricingSource.Dynamic:
      case PricingSource.ManualEntry:
        this.frmQuoteConfiguration.get('contractLengths').enable();
        this.frmQuoteConfiguration.get('annualUsage').disable();
        break;
    }
  }

  sweetSpotCheckboxChanged(event) {
    this.store.dispatch(new SetSweetSpotCheckBoxState(this.quoteRequestId, event.checked));
  }

  utilityDetailsChanged($event) {
    if (this.pricingSource) {
      this.pricingSource = this.pricingSourceService.fetchPricingSource(
        QuoteRequestType.Matrix,
        UserRole[this.userIdentity.currentOrganisation.role],
        this.utilityHubConfiguration.supplierConfiguration.pricing.powwrSupplier,
        this.commodityCtrl.value
      );
      this.setControlVisibility();
    }
  }

  utilityUomSelected($event) {
    this.uom = $event;
  }

  utilitySelected(utility: string) {
    this.zones$.subscribe((x) => (this.zones = x));
    this.weatherZones = { ...this.utilityZones.find((uz) => uz.utilityName === utility) }.weatherZones;
  }

  selectSingleProduct(productType: ProductTypeConfiguration) {
    if (productType) {
      this.productTypeCtrl.setValue(productType.productType);
      this.productTypeCtrl.disable();
      this.productSelectionChanged(productType.productType);
    }
  }

  private disableForm() {
    this.disabled = true;
    this.frmQuoteConfiguration.disable();
  }

  private loadUtilityInformation() {
    this.loadingUtilities = true;
    this.utilities$ = this.utilityInformationService
      .getUtilityInformation(this.stateCode, this.quoteRequestType, this.commodity, this.zipCode)
      .subscribe((u) => {
        this.utilityZones = u.map(function (utility) {
          return {
            utilityName: utility.utilityShort,
            zones: utility.utilityZoneInformation.map((uzi) => uzi.loadZone),
            weatherZones: utility.utilityZoneInformation.map((uzi) => uzi.weatherZone),
          };
        });
        this.utilities = u.map((utility) => utility.utilityShort);
        this.iso = u[0]?.iso;
        this.loadingUtilities = false;
      });
  }
}

export function removeDuplicates(values) {
  return values.filter((elem, index, self) => {
    return index === self.indexOf(elem);
  });
}
